import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { queryKeys } from '@/lib/query-client';
import type { Quote, QuoteHistory, MarketOverview } from '@/types';

export function useQuote(symbol: string) {
  return useQuery({
    queryKey: queryKeys.quotes.detail(symbol),
    queryFn: async () => {
      const { data } = await apiClient.getQuote(symbol);
      return data as Quote;
    },
    enabled: !!symbol,
  });
}

export function useQuoteHistory(
  symbol: string,
  params?: { start_date?: string; end_date?: string; period?: string }
) {
  return useQuery({
    queryKey: queryKeys.quotes.history(symbol, params as Record<string, string>),
    queryFn: async () => {
      const { data } = await apiClient.getQuoteHistory(symbol, params);
      return data as QuoteHistory;
    },
    enabled: !!symbol,
  });
}

export function useQuoteRealtime(symbol: string) {
  return useQuery({
    queryKey: queryKeys.quotes.realtime(symbol),
    queryFn: async () => {
      const { data } = await apiClient.getQuoteRealtime(symbol);
      return data as Quote;
    },
    enabled: !!symbol,
    refetchInterval: 5000, // Refetch every 5 seconds
  });
}

export function useStockSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.quotes.search(query),
    queryFn: async () => {
      const { data } = await apiClient.searchStocks(query);
      return data as Quote[];
    },
    enabled: query.length >= 2,
  });
}

// ─── Universal search (stocks + sectors)(2026-05-28)──────────────────
// Header 搜索框专用,扩展支持板块(stock.sector_catalog)。stock-only 表单
// picker 仍用 useStockSearch,以免下拉里混入板块项。

export interface UniversalStockHit {
  kind: 'stock';
  symbol: string;
  name: string;
  close?: number | null;
  change_percent?: number | null;
}

export interface UniversalSectorHit {
  kind: 'sector';
  sector_name: string;
  sector_type: string;       // industry / concept / region
  sector_code?: string | null;
  member_count: number;
  health_status?: string | null;
}

export interface UniversalSearchResult {
  query: string;
  stocks: UniversalStockHit[];
  sectors: UniversalSectorHit[];
}

export function useUniversalSearch(query: string) {
  return useQuery({
    queryKey: ['search', 'universal', query] as const,
    queryFn: async () => {
      const { data } = await apiClient.searchUniversal(query);
      return data as UniversalSearchResult;
    },
    enabled: query.length >= 2,
    staleTime: 60_000,
  });
}

export function useMarketOverview() {
  return useQuery({
    queryKey: queryKeys.quotes.market(),
    queryFn: async () => {
      const { data } = await apiClient.getMarketOverview();
      return data as MarketOverview;
    },
    refetchInterval: 5000, // Refetch every 5 seconds
  });
}
